<template>
  <div class="two-thumbs-slider">
    <div class="two-thumbs-slider__sliders">
      <div class="sliders__track">
        <div class="track__value-wrapper">
          <div class="track__value" :style="trackValueStyle" />
        </div>
      </div>
      <div class="sliders__slider-wrapper sliders__slider-wrapper_min">
        <input
          type="range"
          class="sliders__slider"
          :value="minValue"
          :step="step"
          :min="min"
          :max="max"
          @input="setMinValue"
          @change="handleChange()"
        >
      </div>
      <div class="sliders__slider-wrapper sliders__slider-wrapper_max">
        <input
          type="range"
          class="sliders__slider"
          :value="maxValue"
          :step="step"
          :min="min"
          :max="max"
          @input="setMaxValue"
          @change="handleChange()"
        >
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from '@nuxtjs/composition-api'

export default {
  name: 'TwoThumbsSlider',
  props: {
    value: {
      type: Array,
      default: null
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    }
  },
  emits: ['input', 'change'],
  setup (props, ctx) {
    const initialMinValue = props.value?.[0] ?? props.min
    const initialMaxValue = props.value?.[1] ?? props.max

    const minValue = ref(initialMinValue)
    const maxValue = ref(initialMaxValue)

    const trackValueStyle = computed(() => {
      const left = (((minValue.value - props.min) / (props.max - props.min)) * 100) + '%'
      const right = ((1 - (maxValue.value - props.min) / (props.max - props.min)) * 100) + '%'

      return {
        left,
        right
      }
    })

    const handleChange = () => {
      ctx.emit('change', [minValue.value, maxValue.value])
    }

    const _handleInput = (valueRef, compareTo, limitCheck, limiter) => {
      let beyondLimit = false

      return (evt) => {
        const { target, target: { value: originalValue } } = evt

        const value = Number(originalValue)

        if (limitCheck(value, compareTo.value)) {
          target.value = compareTo.value
          evt.preventDefault()

          if (beyondLimit) return

          !beyondLimit && (beyondLimit = true)
        } else {
          beyondLimit && (beyondLimit = false)
        }

        valueRef.value = limiter(value, compareTo.value)

        ctx.emit('input', [minValue.value, maxValue.value])
      }
    }

    const setMinValue = _handleInput(minValue, maxValue, (min, max) => min > max, Math.min)
    const setMaxValue = _handleInput(maxValue, minValue, (max, min) => max < min, Math.max)

    const clamp = (val, min, max) => Math.min(max, Math.max(val, min))

    watch(() => props.value, (newVal, oldVal) => {
      if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) return

      if (newVal[0] === oldVal[0]) {
        maxValue.value = clamp(newVal[1], minValue.value, props.max)
      } else if (newVal[1] === oldVal[1]) {
        minValue.value = clamp(newVal[0], props.min, maxValue.value)
      } else if (newVal[0] > newVal[1]) {
        minValue.value = props.min
        maxValue.value = props.max
      } else {
        minValue.value = clamp(newVal[0], props.min, props.max)
        maxValue.value = clamp(newVal[1], props.min, props.max)
      }

      ctx.emit('input', [minValue.value, maxValue.value])
    })

    return {
      minValue,
      maxValue,
      trackValueStyle,
      setMinValue,
      setMaxValue,
      handleChange
    }
  }
}
</script>
